import { useEffect, useState } from 'react';

import AlmostFive from 'components/home/almost-five/almost-five';
import BlogSectionOne from 'components/landing/blog-section/templates/blog-section-template-01';
import BlogSectionThree from 'components/landing/blog-section/templates/blog-section-template-03';
import BlogSectionTwo from 'components/landing/blog-section/templates/blog-section-template-02';
import { cleanDictForJson } from 'utils/secure-json';
import CTABannerContainer from 'components/home/cta-sticky-banner/cta-banner-container';
import CustomContent from 'components/landing/custom-content/custom-content';
import CustomHead from 'core/custom-head';
import Deductibles from 'components/landing/deductibles/deductibles';
import Discount from 'components/landing/discount/discount';
import dynamic from 'next/dynamic';
import FindBestPrice from 'components/landing/find-best-price/find-best-price-v2';
import Footer from 'components/footer/footer';
import getCarName from 'utils/get-car-name';
import getConstants from 'utils/query-data-parser/landing/get-constant';
import Header from 'components/home/header';
import { Header as HeaderSection } from 'components/header/header';
import InformationLinks from 'components/landing/information-links/information-links';
import Journey from 'components/landing/journey/journey';
import LandingContextProvider from 'components/landing/context/landing-context-provider';
import LinkBag from 'components/landing/link-bag/link-bag';
import ListSection from 'components/landing/list-section/list-section';
import ModifyLocationForm from 'components/landing/modify-location/modify-location-form';
import { OpenNavContextProvieder } from 'context/OpenNavContext';
import { PageInfoContextProvider } from 'context/PageInfoContext';
import PremiumPrice from 'components/landing/premium-price/premium-price';
import PropTypes from 'prop-types';
import RelatedNoteContainer from 'components/landing/related-note-list/related-note-container';
import SchemaArticle from 'components/seo/schema/schema-article';
import ScriptContainer from 'components/container/script-container';
import Scroller from 'components/home/scroller';
import SeoTracking from 'components/seo/tracking/seo-tracking';
import TableLinkComponent from 'components/landing/index/table-link-component';
import TableResume from 'components/landing/index/table-resume';
import Testimonial from 'components/home/testimonial/testimonial';
import TopRatedInsurance from 'components/landing/top-rated-insurance/top-rated-insurance';
import { useScroll } from 'hooks';
import withHydrationOnDemand from 'react-hydration-on-demand';

const Top5 = dynamic(() => import('components/landing/top-5/top-5'));
const HorizontalTable = dynamic(() => import('components/landing/tables/horizontal-table/horizontal-table'));
const JourneyH = withHydrationOnDemand({ on: ['visible'] })(Journey);
const DiscountH = withHydrationOnDemand({ on: ['visible'] })(Discount);
const PremiumPriceH = withHydrationOnDemand({ on: ['visible'] })(PremiumPrice);
const DeductiblesH = withHydrationOnDemand({ on: ['visible'] })(Deductibles);
const AlmostFiveH = withHydrationOnDemand({ on: ['visible'] })(AlmostFive);
const TopRatedInsuranceH = withHydrationOnDemand({ on: ['visible'] })(TopRatedInsurance);
const InformationLinksH = withHydrationOnDemand({ on: ['visible'] })(InformationLinks);
const ModifyLocationFormH = withHydrationOnDemand({ on: ['visible'] })(ModifyLocationForm);
const TestimonialH = withHydrationOnDemand({ on: ['visible'] })(Testimonial);
const ListSectionH = withHydrationOnDemand({ on: ['visible'] })(ListSection);
const FooterH = withHydrationOnDemand({ on: ['visible'] })(Footer);
const CustomContentH = withHydrationOnDemand({ on: ['visible'] })(CustomContent);
const RelatedNoteContainerH = withHydrationOnDemand({ on: ['visible'] })(RelatedNoteContainer);
const BlogSectionOneH = withHydrationOnDemand({ on: ['visible'] })(BlogSectionOne);
const BlogSectionTwoH = withHydrationOnDemand({ on: ['visible'] })(BlogSectionTwo);
const BlogSectionThreeH = withHydrationOnDemand({ on: ['visible'] })(BlogSectionThree);
const LinkBagH = withHydrationOnDemand({ on: ['visible'] })(LinkBag);
const FindBestPriceH = withHydrationOnDemand({ on: ['visible'] })(FindBestPrice);

const Landing = ({ culture, carName, payload }) => {
    const [loadTotalPage, setLoadTotalPage] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            setLoadTotalPage(true);
        }, 3000);
    }, []);

    useScroll((scroll) => {
        if (scroll > 0 && !loadTotalPage) {
            setLoadTotalPage(true);
        }
    }, [loadTotalPage]);

    return (
        <ScriptContainer
            breadcrumbList={payload.page_context.page_breadcrumb_list}
            delayLowPriorityScript={5000}
        >
            <SeoTracking
                templateName={payload.page_context.page_config.utm_campaign || 'contentap'}
                pageCode={payload.page_context.code}
            />
            <SchemaArticle pageInfo={payload} />
            <CustomHead pageInfo={payload} />
            <PageInfoContextProvider data={payload}>
                <LandingContextProvider>
                    <OpenNavContextProvieder>
                        <CTABannerContainer hideMobile />
                        <Scroller />
                        <Header />
                        <HeaderSection />
                        <FindBestPriceH showBreadcrumb />
                        <TableResume>
                            <TableLinkComponent id="horizontal-table-section" titleList={[4]}>
                                {loadTotalPage && (
                                    <HorizontalTable
                                        pageCode={payload.page_context.code}
                                        culture={culture}
                                        discount={payload.page_context.page_config.env.discount}
                                        carName={carName}
                                        disableGradient={false}
                                        disableTopPadding={false}
                                    />
                                )}
                            </TableLinkComponent>
                            <TableLinkComponent id="journey-section" titleList={[1]}>
                                <JourneyH />
                            </TableLinkComponent>
                            <TableLinkComponent id="discount-section" titleList={[2]}>
                                <DiscountH data={payload.page_context.page_dinamic_data.discount_list} />
                            </TableLinkComponent>
                            <TableLinkComponent id="top-5-section" titleList={[3]}>
                                {loadTotalPage && (
                                    <Top5
                                        pageCode={payload.page_context.code}
                                        discount={payload.page_context.page_config.env.discount}
                                    />
                                )}
                            </TableLinkComponent>
                            <CustomContentH content={payload.page_context.content} />
                            <RelatedNoteContainerH pageCode={payload.page_context.code} />
                            <TableLinkComponent id="premium-price-section" titleList={[5]}>
                                <PremiumPriceH data={payload.page_context.page_dinamic_data.car_quotation_range} />
                            </TableLinkComponent>
                            <TableLinkComponent id="deductibles-section" titleList={[6]}>
                                <DeductiblesH />
                            </TableLinkComponent>
                            <AlmostFiveH whiteBackground />
                            <TableLinkComponent id="top-rated-insurance-section" titleList={[7]}>
                                <TopRatedInsuranceH
                                    pageCode={payload.page_context.code}
                                    template={payload.template_type}
                                    companyRating={payload.page_context.page_dinamic_data.insurance_company_rating}
                                />
                            </TableLinkComponent>
                            <TableLinkComponent id="information-links-section" titleList={[8]}>
                                <InformationLinksH data={payload.page_context.page_dinamic_data.link_list} />
                            </TableLinkComponent>
                            <TableLinkComponent id="modify-location-section" titleList={[9]}>
                                <ModifyLocationFormH />
                            </TableLinkComponent>
                            <TableLinkComponent id="blog-one-section" titleList={[10]}>
                                <BlogSectionOneH />
                            </TableLinkComponent>
                            <TableLinkComponent id="blog-two-section" titleList={[11]}>
                                <BlogSectionTwoH />
                            </TableLinkComponent>
                            <TableLinkComponent id="blog-three-section" titleList={[12]}>
                                <BlogSectionThreeH />
                            </TableLinkComponent>
                            <ListSectionH
                                relatedPageList={payload.page_context.random_car_page_list}
                                sectionTitle="insurance-car-landing-car"
                                withoutParent
                            />
                            <TestimonialH />
                            <ListSectionH
                                relatedPageList={payload.page_context.related_pages}
                                parentPage={payload.page_context.parent_page}
                                sectionType={payload.template_type}
                            />
                            <LinkBagH />
                        </TableResume>
                        <FooterH />
                    </OpenNavContextProvieder>
                </LandingContextProvider>
            </PageInfoContextProvider>
        </ScriptContainer>
    );
};

export async function getServerSideProps ({ req }) {
    const data = req.apiData.data;
    const culture = data.page_context.page_lang;

    const payload = {
        ...data,
        ...getConstants(data.page_context)
    };

    const carName = getCarName(
        payload.page_context.deep_level,
        payload,
        payload.template_type
    );

    return {
        props: {
            messages: (
                await import(`../messages/insurance-location/${culture}.json`)
            ).default,
            locale: culture.split('-')[1],
            timeZone: req.apiData.timeZone,
            payload: cleanDictForJson(payload),
            culture,
            carName
        }
    };
};

Landing.propTypes = {
    culture: PropTypes.string,
    payload: PropTypes.object,
    carName: PropTypes.string
};

export default Landing;
